import type { LogicFilterLeaf } from '../../fetchSurvey/types/survey';
import type { CustomFieldValue, CustomFieldDateTimeValue } from '../types/customFieldValues';
import type { LogicContext, ResolvedFieldValue } from '../types';
import { LOGIC_FIELDS } from '../constants/logicFields';
import { normalizeSelectedOptions, normalizeText } from './normalizeFieldAnswers';
import { toNumber } from './resolveFieldValue';
// poch ms = milliseconds since January 1, 1970 UTC (Unix timestamp). 
// Logic rules store datetime thresholds the same way, e.g. '1781029800000' in the filter.
// Clients might send datetimes in different shapes. parseEpochMs converts any supported input into one comparable number:
function parseEpochMs(value: CustomFieldDateTimeValue): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return null;
    }

    const numeric = Number(trimmed);
    if (Number.isFinite(numeric) && /^\d+$/.test(trimmed)) {
      return numeric;
    }

    const parsed = Date.parse(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

export function normalizeDateTimeValue(
  value: CustomFieldValue | undefined,
  _timezone?: string
): number | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (Array.isArray(value)) {
    return undefined;
  }

  const epoch = parseEpochMs(value);
  return epoch === null ? undefined : epoch;
}

export function resolveCustomFieldValue(
  field: string | undefined,
  customFieldId: string,
  context: LogicContext,
  filter?: LogicFilterLeaf
): ResolvedFieldValue {
  const raw = context.customFieldValues?.[customFieldId];

  if (raw === undefined || raw === null) {
    return undefined;
  }

  switch (field) {
    case LOGIC_FIELDS.TEXT:
    case LOGIC_FIELDS.CASE_SENSITIVE_TEXT:
      return normalizeText(raw as CustomFieldValue);

    case LOGIC_FIELDS.NUMBER:
      return toNumber(raw);

    case LOGIC_FIELDS.SELECTED_OPTIONS:
      return normalizeSelectedOptions(raw as CustomFieldValue);

    case LOGIC_FIELDS.SELECTED_DATETIME:
      return normalizeDateTimeValue(raw, filter?.details?.valueAdditional?.timezone);

    default:
      return raw as ResolvedFieldValue;
  }
}

export function isCustomFieldValueMissing(
  field: string | undefined,
  customFieldId: string,
  context: LogicContext,
  filter?: LogicFilterLeaf
): boolean {
  const raw = context.customFieldValues?.[customFieldId];

  if (raw === undefined || raw === null) {
    return true;
  }

  if (field === LOGIC_FIELDS.NUMBER) {
    if (typeof raw === 'string' && raw.trim() === '') {
      return true;
    }
    return false;
  }

  if (field === LOGIC_FIELDS.SELECTED_OPTIONS) {
    return normalizeSelectedOptions(raw as CustomFieldValue).length === 0;
  }

  if (field === LOGIC_FIELDS.SELECTED_DATETIME) {
    return normalizeDateTimeValue(raw, filter?.details?.valueAdditional?.timezone) === undefined;
  }

  if (field === LOGIC_FIELDS.TEXT || field === LOGIC_FIELDS.CASE_SENSITIVE_TEXT) {
    return normalizeText(raw as CustomFieldValue) === undefined || normalizeText(raw as CustomFieldValue) === '';
  }

  const resolved = resolveCustomFieldValue(field, customFieldId, context, filter);
  if (resolved === undefined || resolved === null || resolved === '') {
    return true;
  }
  if (Array.isArray(resolved) && resolved.length === 0) {
    return true;
  }

  return false;
}
